import { useQuery } from 'react-query';
import { toast } from 'react-toastify';
import useAuthStore from '../store/authStore';
import { getAllAuthors, getAuthorById, getEbooksByAuthor } from '../api/authorApi';
import React from 'react';


export const useAuthors = () => {
  const { setError } = useAuthStore();

  const {
    data: authors,
    isLoading,
    error,
    refetch
  } = useQuery('authors', getAllAuthors, {
    onError: (error) => {
      const errorMessage = error?.response?.data?.message || 'Failed to fetch authors';
      setError(errorMessage);
      toast.error(errorMessage);
    },
    retry: false,
    staleTime: 300000, // Consider data fresh for 5 minutes
    cacheTime: 3600000, // Keep data in cache for 1 hour
  });

  return {
    authors,
    isLoading,
    error,
    refetchAuthors: refetch
  };
};

export const useAuthor = (authorId) => {
  const { setError } = useAuthStore();

  const {
    data: author,
    isLoading,
    error,
    refetch
  } = useQuery(
    ['author', authorId],
    () => getAuthorById(authorId),
    {
      onError: (error) => {
        console.error('Query error:', error);
        const errorMessage = error?.response?.data?.message || 'Failed to fetch author details';
        setError(errorMessage);
        toast.error(errorMessage);
      },
      retry: 1,
      staleTime: 300000,
      cacheTime: 3600000,
      enabled: authorId !== undefined && authorId !== null,
    }
  );

  // Add some debugging logs
  React.useEffect(() => {
    console.log('Author ID:', authorId);
    console.log('Is query enabled:', authorId !== undefined && authorId !== null);
  }, [authorId]);

  return {
    author,
    isLoading,
    error,
    refetchAuthor: refetch
  };
};

export const useAuthorEbooks = (userId, page = 1, limit = 10, orderBy = 'created_at', order = 'DESC') => {
  const { setError } = useAuthStore();

  const {
    data,
    isLoading,
    error,
    refetch,
    isPreviousData
  } = useQuery(
    ['authorEbooks', userId, page, limit, orderBy, order],
    () => getEbooksByAuthor(userId, page, limit, orderBy, order),
    {
      onError: (error) => {
        console.error('Query error:', error);
        const errorMessage = error?.response?.data?.message || 'Failed to fetch author ebooks';
        setError(errorMessage);
        toast.error(errorMessage);
      },
      retry: 1,
      staleTime: 60000, // 1 minute
      keepPreviousData: true, // Keep previous data while new data is loading
      enabled: userId !== undefined && userId !== null,
    }
  );

  // Extract ebooks and pagination from response
  const ebooks = data?.data || [];
  const pagination = data?.pagination || {
    total: 0,
    totalPages: 0,
    currentPage: page,
    limit
  };

  return {
    ebooks,
    pagination,
    isLoading,
    error,
    isPreviousData,
    refetchEbooks: refetch
  };
};